import React, { useEffect, useState } from 'react'

import {
  Box,
  Color,
  DisplayPosition,
  Icon,
  Overflow,
  withCitrusTheme,
  ZIndex,
} from '@gousto-internal/citrus-react'

import { DeviceType, useDeviceType } from '@library/dom'

import { useNumPortions } from 'hooks/basket/useNumPortions'
import { useBoxes } from 'hooks/menu-boxes/useBoxes'
import { useMaxRecipesForPortion } from 'hooks/menu-boxes/useMaxRecipesForPortion'

import { RecipesInBasketProgressTreatmentContent } from './RecipesInBasketProgressTreatmentContent'

type RecipesInBasketProgressTreatmentPresentationProps = {
  selectedRecipesCount: number
  onClose: (maxRecipes: number, percentage: number) => void
}

const CloseButton = withCitrusTheme('button', (theme) => ({
  appearance: 'none',
  background: theme.color(Color.White),
  border: 0,
  ...theme.radius('round'),
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: `${theme.spacing(5)}px`,
  height: `${theme.spacing(5)}px`,
  padding: 0,
  position: 'absolute',
  // -10px (half the button) overhangs the card corner; no spacing token maps to 10px
  top: '-10px',
  left: '-10px',
  boxShadow: '0 2px 4px rgba(33, 41, 48, 0.16)',
  zIndex: 1,
}))

const RecipesInBasketProgressTreatmentPresentation = ({
  selectedRecipesCount,
  onClose,
}: RecipesInBasketProgressTreatmentPresentationProps) => {
  const { isBoxesLoading } = useBoxes()
  const { numPortions } = useNumPortions()
  const maxRecipesNum = useMaxRecipesForPortion(numPortions)
  const [isDismissed, setIsDismissed] = useState(false)

  const percentage =
    maxRecipesNum > 0 ? Math.round((selectedRecipesCount / maxRecipesNum) * 100) : 0

  const deviceType = useDeviceType()
  const isMobileDevice = deviceType === DeviceType.MOBILE

  useEffect(() => {
    if (!selectedRecipesCount) {
      setIsDismissed(false)
    }
  }, [selectedRecipesCount])

  if (!selectedRecipesCount || isBoxesLoading || isDismissed) {
    return null
  }

  return (
    <Box
      data-no-code={isMobileDevice ? 'boxProgressAlertMobile' : undefined}
      bg={Color.White}
      borderRadius={1.5}
      boxShadow="0 4px 4px rgba(0, 0, 0, 0.25)"
      position={DisplayPosition.Fixed}
      bottom="8rem"
      left={['16px', '24px']}
      width={['calc(100vw - 32px)', '22rem']}
      maxWidth={['none', '22rem']}
      overflowX={Overflow.Visible}
      overflowY={Overflow.Visible}
      zIndex={ZIndex.Top}
    >
      <CloseButton
        aria-label="Dismiss recipes in basket progress"
        type="button"
        onClick={() => {
          setIsDismissed(true)
          onClose(maxRecipesNum, percentage)
        }}
        data-no-code="boxProgressClose"
      >
        <Icon name="close" size={4} color={Color.ColdGrey_800} />
      </CloseButton>
      <Box paddingH={3} paddingV={2}>
        <RecipesInBasketProgressTreatmentContent
          selectedRecipesCount={selectedRecipesCount}
          percentage={percentage}
        />
      </Box>
    </Box>
  )
}

export { RecipesInBasketProgressTreatmentPresentation }
